import { FC, useContext, useEffect, useState } from 'react';
import LoaderImage from '@assets/loader.gif';
import * as types from '@models/types';
import { HeatMapContext } from '@providers/HeatMap';

import HeatMapChart from '@components/HeatMapChart';
import './HeatMapTab.scss';
import Cookies from 'js-cookie';

type HeatMapTabProps = {
    selectedProfile: types.SelectedProfileTypes;
    isLoadingTabContent: boolean|"ongoing";
    handleClick: (a: types.HeatMapDataTypes) => void;
};
const HeatMapTab: FC<HeatMapTabProps> = ({
    selectedProfile,
    isLoadingTabContent,
    handleClick
}) => {
    const {
        heatMapData
    } = useContext(HeatMapContext);

    const [heatMapXAxisLabels, setHeatMapXAxisLabels] = useState<any[]>([]);
    const [heatMapYAxisLabels, setHeatMapYAxisLabels] = useState<any[]>([]);

    useEffect(() => {
        var heatMapSectors: string[] = [];
        var heatMapOccupations: string[] = [];
        if (heatMapData && heatMapData.length > 0) {
            //get sorted Data
            const sortedHeatmap = heatMapData.sort((a: any, b: any) => b.value - a.value)
            
            // In case of a single selection for occupation, order the values horizontally
            if (selectedProfile.occupation.length === 1) {
                // From sortedHeatmap get all sorted values which contains the title of occupation then sort the values and return list of sectors
                heatMapSectors = sortedHeatmap.filter((heatmap) => {
                    return heatmap.occupation === selectedProfile.occupation[0].title && selectedProfile.sector.map((sector) => sector.title).indexOf(heatmap.sector) > -1;
                }).sort((a, b) => b.value - a.value)?.map((e: any) => e.sector);
                // For all of the rest of sectors that doesn't exist in the sortedHeatmap, add them at the end of the list 
                heatMapSectors.push(...selectedProfile.sector.filter((sector) => {
                    return heatMapSectors.map((sector) => sector).indexOf(sector.title) === -1;
                })?.map((e: any) => e.title));
                // Finally, push the single occupation to heatMapOccupations
                heatMapOccupations.push(selectedProfile.occupation[0].title);
            }
            else {
                // In case of a single selection for sector, order the values vertically
                if (selectedProfile.sector.length === 1) {
                    // From sortedHeatmap get all sorted values which contains the title of sector then sort the values and return list of occupations
                    heatMapOccupations = sortedHeatmap.filter((heatmap) => {
                        return heatmap.sector === selectedProfile.sector[0].title && selectedProfile.occupation.map((occupation) => occupation.title).indexOf(heatmap.occupation) > -1;
                    }).sort((a, b) => a.value - b.value)?.map((e: any) => e.occupation);
                    // For all of the rest of occupations that doesn't exist in the sortedHeatmap, add them at the end of the list
                    heatMapOccupations.splice(0, 0, ...selectedProfile.occupation.filter((occupation) => {
                        return heatMapOccupations.map((occupation) => occupation).indexOf(occupation.title) === -1;
                    })?.map((e: any) => e.title));
                    // Finally, push the single sector to heatMapOccupations
                    heatMapSectors.push(selectedProfile.sector[0].title);
                }
                else {
                    // In case of multiple selections for both sector and occupation
                    const sortedSectorsFromHeatmap = [...new Set(sortedHeatmap.map((e: any) => e.sector))] //from heatmap data
                    const sortedOccupationsFromHeatmap = [...new Set(sortedHeatmap.map((e: any) => e.occupation))] //from heatmap data

                    const getSelectedSectorsWithOrderSorted = selectedProfile.sector.map(e => ({
                        title: e.title,
                        order: (sortedSectorsFromHeatmap.indexOf(e.title) < 0) ? 1000 : sortedSectorsFromHeatmap.indexOf(e.title)
                    })).sort((a, b) => a.order - b.order)
                    heatMapSectors = getSelectedSectorsWithOrderSorted?.map((e: any) => e.title)

                    const getSelectedOccupationsWithOrderSorted = selectedProfile.occupation.map(e => ({
                        title: e.title,
                        order: (sortedOccupationsFromHeatmap.indexOf(e.title) < 0) ? 1000 : sortedOccupationsFromHeatmap.indexOf(e.title)
                    })).sort((a, b) => b.order - a.order)
                    heatMapOccupations = getSelectedOccupationsWithOrderSorted?.map((e: any) => e.title)
                }
            }
        }

        const xAxisData = heatMapSectors.map(sector => ({
            sector,
        }));

        const yAxisData = heatMapOccupations.map(occupation => ({
            occupation,
        }));

        setHeatMapXAxisLabels(xAxisData);
        setHeatMapYAxisLabels(yAxisData);
    }, [heatMapData, selectedProfile.occupation, selectedProfile.sector])

    return isLoadingTabContent ? <div><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div> : <div className="heatmap-tab">
        <div className="widget-component">
            <h4 className="text-center pb-2">This heatmap shows the number of job posts by occupation and sector for the time period {selectedProfile.fromTimeline?.title} to {selectedProfile.toTimeline?.title ? selectedProfile.toTimeline?.title : "present"}</h4>
            {heatMapData && heatMapData.length > 0 && heatMapXAxisLabels && heatMapXAxisLabels.length > 0 && heatMapYAxisLabels && heatMapYAxisLabels.length > 0 ?
                <HeatMapChart
                    name="heatmap"
                    data={heatMapData}
                    xAxisLabels={heatMapXAxisLabels}
                    yAxisLabels={heatMapYAxisLabels}
                    xAxisFieldName="sector"
                    yAxisFieldName="occupation"
                    titleX="Sectors"
                    titleY="Occupations"
                    valueFieldName="value"
                    height={(selectedProfile.occupation.length > 3) ? (150 + (selectedProfile.occupation.length * ((Number(Cookies.get('globalFontSize') ? (Number(Cookies.get('globalFontSize')) * 4.5) : 60))))) : 500}
                    width={(selectedProfile.sector.length > 5) ? 150 + (selectedProfile.sector.length * 180) : "100%"}
                    handleClick={handleClick}
                />
                :
                <div className="na">N/A</div>
            }
        </div>
    </div>
};

export default HeatMapTab;
